/**
 * Action bar: every hotbar key (1–6 rotation, Q / E, R, F and the G action, also Shift+F), built
 * from kit IconSlots (painterly icons, cooldown / GCD sweeps on the sub-tick clock, charges,
 * keybinds, usability, instant press), plus the cluster highlights from cluster/derive.ts:
 *  - spell activation (animated border) from the rework's F11 rules (HighlightIndex) or, for
 *    legacy auras, an empowering temporary buff;
 *  - transforms (F2): a button whose base ability is overridden shows the form's icon, name,
 *    cooldown, cost check and tooltip, and glows; the key still sends the base id;
 *  - stack counters (`ui.stackCounterOn`) and empowering-buff stacks in the rank label;
 *  - the soft "lit" rim (conditional / usableIf) and the interrupt-ready ring.
 * The highlight state is computed once per frame for the player (not per slot).
 *
 * Used by the skill cluster (cluster/skillCluster.ts) and, if no widget replaces it, by the
 * legacy dock in hud.ts.
 *
 * Latency-probe contract (game/session.ts): container `.actionbar`, one `.action` per
 * UI_BIND_KEYS entry in order, slot classes `pressed` / `casting` / `queued`, `.action-gcd.on`.
 * Prediction: the predicted GCD and predicted cast / queue (game/prediction.ts localFeedback)
 * show on the slots the same frame as the key press, before the sim applies the command.
 */
import type { AbilityDef, CastResult, ContentRegistry, DeepReadonly, SimQueries, UnitId, World } from '@keystone/sim';
import { type UiBind, UI_BIND_KEYS, abilityForBind, buildCastCommand } from '../input/bindings';
import { hudNow, localFeedback } from '../game/prediction';
import { SCHOOL_COLOR } from '../render/palette';
import { el } from './dom';
import { fieldView } from '../world/fieldView'; // +field
import { IconSlot, PRESS_FLASH_MS } from './kit/iconSlot';
import type { IconSizeName } from './kit/tokens';
import type { CooldownInput } from './kit/slotState';
import { type TooltipLive, actionTooltipModel } from './tooltip/hud';
import {
  type AbilityProcSpec,
  type ClusterSlotView,
  type SlotDeriveInput,
  EmpowerIndex,
  HighlightIndex,
  abilityProcSpec,
  conditionLit,
  deriveSlot,
  emptyClusterSlotView,
  interruptWanted,
  shownAbilityId,
  slotRankLabel,
} from './cluster/derive';

export { PRESS_FLASH_MS };

interface Slot {
  bind: UiBind;
  view: IconSlot;
  state: ClusterSlotView;
  /** Bound (base) ability id; what the key sends. */
  baseId: string | null;
  /** Shown ability id: the transformed form while overridden, else the base id (tooltip, icon). */
  abilityId: string | null;
  spec: AbilityProcSpec | null;
  kick: boolean;
  rank: string;
  /** +field: hero level that unlocks the bound ability (0 = usable), and the new-ability glow. */
  lock: number;
  fresh: boolean;
  /** +classes: drawn at all (empty keys and later unlocks are hidden). */
  shown?: boolean;
}

export interface ActionBarOptions {
  /** Icon size of the slots (default md). */
  size?: IconSizeName;
  /** Binds drawn as round slots (the cluster draws G as a round bar-end slot). */
  roundBinds?: readonly UiBind[];
  /** Key label per bind (default: the bind itself). */
  keyLabel?: (bind: UiBind) => string;
}

/** The live action bar (one HUD at a time), for instant press feedback from the key handler. */
let activeBar: ActionBar | null = null;

/** Show the pressed state on `bind`'s button right now (called from the keydown handler, before the sim sees the press). */
export function flashActionBind(bind: UiBind, now: number): void {
  activeBar?.flash(bind, now);
}

export class ActionBar {
  readonly root: HTMLElement;
  private readonly slots: Slot[];
  private gcdStart = 0;
  private gcdUntil = 0;
  /** Latest frame state for the hover tooltip. */
  private tip: TooltipLive | null = null;
  private empower: EmpowerIndex | null = null;
  private highlights: HighlightIndex | null = null;
  private empowerContent: ContentRegistry | null = null;
  private known: (id: string) => boolean = () => false;
  // Reused per-slot inputs (no per-frame allocation).
  private readonly cdIn: CooldownInput = { now: 0, tickTime: 0, remainAtTick: 0, cd: undefined, defDurationMs: undefined, defCharges: undefined, gcdMs: 0, gcdStart: 0, gcdUntil: 0 };
  private readonly dIn: SlotDeriveInput = {
    cooldown: this.cdIn,
    check: null,
    casting: false,
    queued: false,
    empowerStacks: 0,
    conditionLit: false,
    interruptWanted: false,
    highlighted: false,
    stackCounter: 0,
    transformed: false,
  };

  constructor(parent: HTMLElement, onPress: (bind: UiBind) => void, opts: ActionBarOptions = {}) {
    this.root = el('div', 'actionbar', parent);
    const round = new Set(opts.roundBinds ?? []);
    this.slots = UI_BIND_KEYS.map((bind) => {
      const slot: Slot = { bind, view: null as unknown as IconSlot, state: emptyClusterSlotView(), baseId: null, abilityId: null, spec: null, kick: false, rank: '', lock: 0, fresh: false };
      slot.view = new IconSlot(this.root, {
        size: opts.size ?? 'md',
        className: `action bind-${bind}${round.has(bind) ? ' is-round' : ''}`,
        keybind: opts.keyLabel ? opts.keyLabel(bind) : bind,
        // pointerdown, not click: the press (and its pressed state) lands on the down stroke.
        onPress: () => onPress(bind),
        tooltip: () => actionTooltipModel(this.tip, slot.abilityId, bind, slot.baseId),
      });
      return slot;
    });
    activeBar = this;
  }

  /** Pressed visual for a bind, immediately (no waiting for the next frame or the sim). */
  flash(bind: UiBind, now: number): void {
    for (const s of this.slots) if (s.bind === bind) s.view.press(now);
  }

  /** `targetId` must be resolved the same way the cast command resolves it (see buildCastCommand). */
  update(world: DeepReadonly<World>, content: ContentRegistry, queries: SimQueries, playerId: UnitId, targetId: UnitId | null, hoverId: UnitId | null = null): void {
    const u = world.units[playerId];
    const cls = u?.classId ? content.class(u.classId) : undefined;
    if (!u || !cls) return;
    if (this.tip) {
      this.tip.world = world;
      this.tip.content = content;
      this.tip.queries = queries;
      this.tip.playerId = playerId;
      this.tip.targetId = targetId;
    } else this.tip = { world, content, queries, playerId, targetId };
    if (this.empowerContent !== content) {
      this.empowerContent = content;
      this.empower = new EmpowerIndex((id) => content.aura(id));
      this.highlights = new HighlightIndex((id) => content.aura(id));
      this.known = (id) => !!content.ability(id);
    }
    // Activation glow + stack counters for the player, once per frame (reused containers).
    const hl = this.highlights!.compute(world, playerId, targetId ?? u.targetId);
    // Sweeps run on the sim's sub-tick "present" so they move every frame, not every 50 ms tick.
    const now = hudNow(world);
    const realNow = typeof performance !== 'undefined' ? performance.now() : 0;
    if (u.gcdUntil !== this.gcdUntil) {
      this.gcdStart = world.time;
      this.gcdUntil = u.gcdUntil;
    }
    // A predicted press (not yet applied by the sim) shows its GCD at once.
    const pred = localFeedback.prediction && localFeedback.prediction.actorId === playerId ? localFeedback.prediction : null;
    const pgcd = pred?.gcd && pred.gcd.until > u.gcdUntil ? pred.gcd : null;
    const target = targetId ? world.units[targetId] : undefined;
    const cd = this.cdIn;
    cd.now = now;
    cd.tickTime = world.time;
    cd.gcdStart = pgcd ? pgcd.start : this.gcdStart;
    cd.gcdUntil = pgcd ? pgcd.until : this.gcdUntil;
    const d = this.dIn;
    // +classes: the bar shows only what the hero has: every unlocked ability, plus ONE locked slot
    // (the next to unlock, "Lv N"); empty keys and later unlocks are hidden, so it grows as you level.
    let nextNeed = Infinity;
    let nextBind: UiBind | null = null;
    if (u.level !== undefined) {
      for (const s of this.slots) {
        const id = abilityForBind(cls, s.bind);
        const need = id ? (cls.unlocks?.[id] ?? 0) : 0;
        if (id && need > u.level && need < nextNeed) {
          nextNeed = need;
          nextBind = s.bind;
        }
      }
    }
    for (const s of this.slots) {
      const baseId = abilityForBind(cls, s.bind) ?? '';
      const lockNeed = baseId && u.level !== undefined ? (cls.unlocks?.[baseId] ?? 0) : 0;
      const shown = !!baseId && (lockNeed <= (u.level ?? Infinity) || s.bind === nextBind);
      if (shown !== s.shown) {
        s.shown = shown;
        s.view.root.classList.toggle('hk-hidden', !shown);
      }
      // Transform (F2): the button shows, checks and times the form; the key still sends the base id.
      const id = baseId ? shownAbilityId(u, baseId, this.known) : '';
      const a = id ? content.ability(id) : undefined;
      if (s.abilityId !== id || s.baseId !== baseId) this.assign(s, a, id, baseId);
      if (!a) {
        s.view.render(RESET, realNow);
        this.highlight(s, RESET);
        this.drawLock(s, 0, false);
        continue;
      }
      // +field: locked by hero level (level badge) / just unlocked (glows until first used).
      const need = u.level !== undefined ? (cls.unlocks?.[baseId] ?? 0) : 0;
      this.drawLock(s, need > u.level! ? need : 0, fieldView.newUnlocks.has(baseId));
      let remainAtTick = 0;
      try {
        remainAtTick = queries.cooldownRemaining(playerId, a.id);
      } catch {
        remainAtTick = 0;
      }
      cd.remainAtTick = remainAtTick;
      cd.cd = u.cooldowns[a.id];
      cd.defDurationMs = a.cooldown?.durationMs;
      cd.defCharges = a.cooldown?.charges;
      cd.gcdMs = a.gcdMs ?? 1500;
      // Same target resolution as the key press (mouse-over for friendly spells only), with the
      // form's targeting (a self-cast channel can transform into a targeted finisher).
      const cmd = buildCastCommand(world, a, { actorId: playerId, selectedId: targetId, hoverId, cursorPoint: null, mouseover: true });
      const castTarget = a.targeting === 'ground' || cmd.type !== 'cast' ? null : (cmd.targetId ?? null);
      let r: CastResult | null = null;
      try {
        // The sim resolves the override itself, so ask with the pressed (base) id.
        r = queries.canCast(playerId, baseId, castTarget, null);
      } catch {
        r = null;
      }
      const spec = s.spec!;
      const transformed = id !== baseId;
      d.check = r;
      // Casts and predictions carry the form's id; the spell queue keeps the pressed id.
      d.casting = u.cast?.abilityId === id || (pred?.state === 'start' && !!pred.cast && pred.abilityId === id);
      d.queued = u.queued?.abilityId === baseId || (pred?.state === 'queued' && (pred.pressedId ?? pred.abilityId) === baseId);
      d.empowerStacks = this.empower!.stacks(u, id);
      d.conditionLit = conditionLit(spec, u, target, !!r?.ok);
      d.interruptWanted = interruptWanted(spec, u, target);
      d.highlighted = hl.glow.has(baseId) || (transformed && hl.glow.has(id));
      d.stackCounter = hl.counters.get(baseId) ?? (transformed ? (hl.counters.get(id) ?? 0) : 0);
      d.transformed = transformed;
      const st = deriveSlot(d, s.state);
      s.view.render(st, realNow);
      this.highlight(s, st);
    }
  }

  /** +field: locked slot (greyed, "Lv N" badge) and the new-ability glow. */
  private drawLock(s: Slot, lock: number, fresh: boolean): void {
    if (lock !== s.lock) {
      s.lock = lock;
      s.view.root.classList.toggle('is-locked', lock > 0);
      let badge = s.view.root.querySelector<HTMLElement>('.action-lock');
      if (lock > 0 && !badge) badge = el('span', 'action-lock', s.view.root);
      if (badge) badge.textContent = lock > 0 ? `Lv ${lock}` : '';
    }
    if (fresh !== s.fresh) {
      s.fresh = fresh;
      s.view.root.classList.toggle('is-new', fresh);
    }
  }

  private highlight(s: Slot, st: ClusterSlotView): void {
    s.view.setProc(st.lit);
    s.view.setActivation(st.activation);
    s.view.setTransformed(st.transformed);
    if (st.kick !== s.kick) {
      s.kick = st.kick;
      s.view.root.classList.toggle('is-kick', st.kick);
    }
    const rank = slotRankLabel(st);
    if (rank !== s.rank) {
      s.rank = rank;
      s.view.setRank(rank);
    }
  }

  private assign(s: Slot, a: DeepReadonly<AbilityDef> | AbilityDef | undefined, id: string, baseId: string): void {
    s.abilityId = id;
    s.baseId = baseId;
    s.spec = a ? abilityProcSpec(a) : null;
    s.view.setIcon(a ? id : null, { name: a?.name ?? id, color: a?.ui?.color ?? SCHOOL_COLOR[a?.school ?? 'physical'] });
    s.view.root.classList.toggle('empty', !a);
    // The name for a transformed button (also read by assistive tech; tooltips show the form).
    if (a) s.view.root.setAttribute('aria-label', a.name);
    else s.view.root.removeAttribute('aria-label');
  }
}

const RESET: ClusterSlotView = emptyClusterSlotView();
